import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Select, Drawer, Spin } from "antd";
import {
  PRIORITY_OPTIONS,
  REPEAT_OPTIONS,
  RELATED_TO_OPTIONS,
  TAG_OPTIONS,
  fetchStaffDropdown,
  fetchRelatedOptions,
  saveNewTask,
  buildTaskPayload,
} from "./taskApi";
import Swal from "sweetalert2";

const emptyForm = {
  name: "",
  description: "",
  priority: undefined,
  startdate: "",
  duedate: "",
  repeat_every: undefined,
  rel_id: undefined,
  rel_type: undefined,
  is_public: false,
  billable: false,
  hourly_rate: "",
  assignments: [],
  followers: [],
  tags: [],
};

// rel_types that currently have a live list to fetch
const FETCHABLE_REL_TYPES = [
  "lead",
  "customer",
  "project",
  "estimate",
  "invoice",
  "proposal",
];

// Responsive width for the drawer, matching the old offcanvas breakpoints.
const DrawerResponsiveStyles = () => (
  <style>
    {`
    @media (max-width: 576px) {
      .new-task-drawer .ant-drawer-content-wrapper { width: 90% !important; }
    }
    @media (min-width: 577px) and (max-width: 992px) {
      .new-task-drawer .ant-drawer-content-wrapper { width: 60% !important; }
    }
    @media (min-width: 993px) and (max-width: 1440px) {
      .new-task-drawer .ant-drawer-content-wrapper { width: 45% !important; }
    }
    @media (min-width: 1441px) {
      .new-task-drawer .ant-drawer-content-wrapper { width: 23% !important; }
    }
    `}
  </style>
);

// open: controls drawer visibility
// onClose: called when the user cancels / closes the drawer without saving
// onSuccess: called after a successful save (parent should close + refetch)
const NewTask = ({ open, onClose, onSuccess, type, id }) => {
  const [form, setForm] = useState(emptyForm);
  const [staffOptions, setStaffOptions] = useState([]);
  const [relatedOptions, setRelatedOptions] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [saving, setSaving] = useState(false);
  const isRelatedLocked = Boolean(type && id);
  const [errors, setErrors] = useState({});
  console.log(id, "idddddddddddddddddddddddddddddddddddddd");
  // Load staff once for Assignees / Followers
  useEffect(() => {
    fetchStaffDropdown()
      .then(setStaffOptions)
      .catch((err) => console.error("Failed to load staff dropdown", err));
  }, []);

  // Reset the form each time the drawer is opened fresh
  useEffect(() => {
    if (open) {
      setForm({
        ...emptyForm,
        rel_type: type,
        rel_id: Number(id),
      });
    }
  }, [open, type, id]);

  // Whenever "Related To" changes, load the matching list (project/customer/lead)
  useEffect(() => {
    if (!form.rel_type || !FETCHABLE_REL_TYPES.includes(form.rel_type)) {
      setRelatedOptions([]);
      return;
    }
    setLoadingRelated(true);
    fetchRelatedOptions(form.rel_type)
      .then(setRelatedOptions)
      .catch((err) => console.error("Failed to load related options", err))
      .finally(() => setLoadingRelated(false));
  }, [form.rel_type]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRelTypeChange = (value) => {
    setForm((prev) => ({ ...prev, rel_type: value, rel_id: undefined }));
  };

  const validateTask = () => {
    const newErrors = {};

    if (!form.name?.trim()) {
      newErrors.name = "Task subject is required";
    }

    if (!form.startdate) {
      newErrors.startdate = "Start date is required";
    }

    if (!form.duedate) {
      newErrors.duedate = "Due date is required";
    }

    if (!form.rel_type) {
      newErrors.rel_type = "Related To is required";
    }

    if (form.rel_type && !form.rel_id) {
      newErrors.rel_id = "Related item is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateTask()) {
      return;
    }
    setSaving(true);
    try {
      const payload = buildTaskPayload(form);
      const result = await saveNewTask(payload);
      if (result?.Status) {
        Swal.fire({
          icon: "success",
          title: "Task Added Successfully",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
        setForm(emptyForm);
        await onSuccess?.(result);
      } else {
        console.error("Add task failed", result?.Message);
      }
    } catch (err) {
      console.error("Failed to save task", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DrawerResponsiveStyles />
      {saving && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <Drawer
        title={
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>Add New Task</span>

            <div className="d-flex align-items-center gap-2">
              {/* Close Drawer */}
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor: "#e40d22",
                  color: "#fff",
                  border: "none",
                }}
                title="Close"
              >
                <i className="fa fa-times" />
              </button>

              {/* Save */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor: "#07f083",
                  color: "#fff",
                  border: "none",
                }}
                title="Save"
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  <i className="fa fa-check" />
                )}
              </button>
            </div>
          </div>
        }
        placement="right"
        open={open}
        onClose={onClose}
        rootClassName="new-task-drawer"
        destroyOnClose
        closable={false}
        styles={{
          body: {
            paddingTop: 1,
          },
        }}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="content pt-0">
            <div className="d-flex mb-2 row justify-content-between align-items-center d-none">
              <div className="col-6">
                <label className="form-label">Attachments:</label>
                <input type="file" className="form-control" />
              </div>
            </div>

            <div className="row pt-0">
              <div className="col-12 mb-3">
                <label className="form-label">
                  Subject <span className="text-danger"> *</span>
                </label>

                <input
                  type="text"
                  placeholder="Enter subject"
                  className={`form-control ${errors.name ? "border-danger" : ""}`}
                  value={form.name}
                  onChange={(e) => {
                    updateField("name", e.target.value);

                    if (errors.name) {
                      setErrors((prev) => ({
                        ...prev,
                        name: "",
                      }));
                    }
                  }}
                />

                {errors.name && (
                  <div className="text-danger small mt-1">{errors.name}</div>
                )}
              </div>

              <div className="col-6 mb-3">
                <label className="form-label">
                  Start Date <span className="text-danger"> *</span>
                </label>

                <input
                  type="date"
                  className={`form-control ${errors.startdate ? "border-danger" : ""}`}
                  value={form.startdate}
                  onChange={(e) => {
                    updateField("startdate", e.target.value);

                    if (errors.startdate) {
                      setErrors((prev) => ({
                        ...prev,
                        startdate: "",
                      }));
                    }
                  }}
                />

                {errors.startdate && (
                  <div className="text-danger small mt-1">
                    {errors.startdate}
                  </div>
                )}
              </div>
              <div className="col-6 mb-3">
                <label className="form-label">
                  Due Date <span className="text-danger"> *</span>
                </label>

                <input
                  type="date"
                  className={`form-control ${errors.duedate ? "border-danger" : ""}`}
                  value={form.duedate}
                  onChange={(e) => {
                    updateField("duedate", e.target.value);

                    if (errors.duedate) {
                      setErrors((prev) => ({
                        ...prev,
                        duedate: "",
                      }));
                    }
                  }}
                />

                {errors.duedate && (
                  <div className="text-danger small mt-1">{errors.duedate}</div>
                )}
              </div>

              {form.billable && (
                <div className="col-4 mb-3 d-none">
                  <label className="form-label">Hourly Rate</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={form.hourly_rate}
                    onChange={(e) => updateField("hourly_rate", e.target.value)}
                  />
                </div>
              )}

              <div className="col-4 mb-3 d-none">
                <label className="form-label">Repeat every</label>
                <Select
                  className="w-100"
                  style={{ height: 38 }}
                  placeholder="Repeat Every"
                  value={form.repeat_every}
                  onChange={(value) => updateField("repeat_every", value)}
                  options={REPEAT_OPTIONS}
                />
              </div>

              <div className="col-6 mb-3">
                <label className="form-label">
                  Related To <span className="text-danger"> *</span>
                </label>

                <Select
                  className={`w-100 ${errors.rel_type ? "is-invalid" : ""}`}
                  classNamePrefix="react-select"
                  placeholder="Related To"
                  value={form.rel_type}
                  onChange={(value) => {
                    handleRelTypeChange(value);

                    if (errors.rel_type) {
                      setErrors((prev) => ({
                        ...prev,
                        rel_type: "",
                      }));
                    }
                  }}
                  options={RELATED_TO_OPTIONS}
                  isDisabled={isRelatedLocked}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: errors.rel_type
                        ? "#dc3545"
                        : state.isFocused
                          ? "#86b7fe"
                          : base.borderColor,
                      boxShadow: errors.rel_type
                        ? "0 0 0 0.1rem rgba(220,53,69,.25)"
                        : base.boxShadow,
                      "&:hover": {
                        borderColor: errors.rel_type
                          ? "#dc3545"
                          : base.borderColor,
                      },
                    }),
                    height: 38,
                  }}
                />

                {errors.rel_type && (
                  <div className="text-danger small mt-1">
                    {errors.rel_type}
                  </div>
                )}
              </div>

              {form.rel_type && (
                <div className="col-6 mb-3">
                  <label className="form-label">
                    {RELATED_TO_OPTIONS.find((o) => o.value === form.rel_type)
                      ?.label || "Related Item"}
                    <span className="text-danger"> *</span>
                  </label>

                  <Select
                    className="w-100"
                    placeholder={`Select ${form.rel_type}`}
                    value={form.rel_id}
                    onChange={(value) => {
                      updateField("rel_id", value);

                      if (errors.rel_id) {
                        setErrors((prev) => ({
                          ...prev,
                          rel_id: "",
                        }));
                      }
                    }}
                    loading={loadingRelated}
                    notFoundContent={
                      FETCHABLE_REL_TYPES.includes(form.rel_type)
                        ? undefined
                        : "No list wired up yet"
                    }
                    options={relatedOptions}
                    isDisabled={isRelatedLocked}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: errors.rel_id
                          ? "#dc3545"
                          : state.isFocused
                            ? "#86b7fe"
                            : base.borderColor,
                        boxShadow: errors.rel_id
                          ? "0 0 0 0.1rem rgba(220,53,69,.25)"
                          : base.boxShadow,
                        "&:hover": {
                          borderColor: errors.rel_id
                            ? "#dc3545"
                            : base.borderColor,
                        },
                      }),
                      height: 38,
                    }}
                  />

                  {errors.rel_id && (
                    <div className="text-danger small mt-1">
                      {errors.rel_id}
                    </div>
                  )}
                </div>
              )}

              <div className="col-6 mb-3">
                <label className="form-label">Priority </label>
                <Select
                  className="w-100"
                  style={{ height: 38 }}
                  placeholder="Select Priority"
                  value={form.priority}
                  onChange={(value) => updateField("priority", value)}
                  options={PRIORITY_OPTIONS}
                />
              </div>
              <div className="col-6 mt-5">
                <label className="me-4">
                  <input
                    type="checkbox"
                    name="public"
                    checked={form.is_public}
                    onChange={(e) => updateField("is_public", e.target.checked)}
                  />{" "}
                  Public
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="billable"
                    checked={form.billable}
                    onChange={(e) => updateField("billable", e.target.checked)}
                  />{" "}
                  Billable
                </label>
              </div>

              <div className="col-12 mb-3">
                <label className="form-label">Assignees </label>
                <Select
                  className="w-100"
                  mode="multiple"
                  style={{ height: 38 }}
                  placeholder="Assign To"
                  value={form.assignments}
                  onChange={(value) => updateField("assignments", value)}
                  options={staffOptions}
                />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Followers </label>
                <Select
                  className="w-100"
                  style={{ height: 38 }}
                  mode="multiple"
                  placeholder="Followers"
                  value={form.followers}
                  onChange={(value) => updateField("followers", value)}
                  options={staffOptions}
                />
              </div>
            </div>

            <div className="col-12 d-none">
              <label className="form-label">Tags:</label>
              <Select
                className="w-100"
                style={{ height: 38 }}
                mode="multiple"
                placeholder="tags"
                value={form.tags}
                onChange={(value) => updateField("tags", value)}
                options={TAG_OPTIONS}
              />
            </div>

            <div className="mt-2 mb-3">
              <label className="form-label">Task Description:</label>
              <textarea
                rows={3}
                className="form-control"
                placeholder="Add Description"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
              />
            </div>

            <div className="d-flex justify-content-end d-none">
              <button
                type="button"
                className="btn btn-secondary d-flex mt-3 me-2"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex mt-3"
                disabled={saving}
                onClick={handleSubmit}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </Drawer>
    </>
  );
};

NewTask.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  type: PropTypes.oneOf([
    "lead",
    "customer",
    "project",
    "invoice",
    "proposal",
    "estimate",
    "creditnote",
  ]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NewTask;
